/**
 * @file useExperiencesFacade.hook.ts
 * @path apps/website/src/features/experiences/hooks/useExperiencesFacade.hook.ts
 * @description Consolidated facade combining state + derived data for the experiences list page.
 */
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { ExperienceService } from "../experience.service";

export interface PriceBin {
  count: number;
  minPrice: number;
  maxPrice: number;
  height: number;
}

export const useExperiencesFacade = () => {
  // --- State ---
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const filterVersion = useRef(0);

  // Scroll to top of list when filters change
  useEffect(() => {
    if (filterVersion.current > 0) {
      const el = document.getElementById("experiences-content");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    filterVersion.current++;
  }, [activeCategory, searchQuery, maxPrice]);

  const resetFilters = useCallback(() => {
    setActiveCategory("All");
    setSearchQuery("");
    setMaxPrice(null);
  }, []);

  // --- Derived Data ---
  const allItems = useMemo(() => ExperienceService.getAllExperiences(), []);

  const absoluteMaxPrice = useMemo(() => {
    return Math.max(...allItems.map((item) => item.price.amount), 0);
  }, [allItems]);

  const priceHistogram = useMemo<PriceBin[]>(() => {
    const BIN_COUNT = 9;
    const bins: PriceBin[] = [];
    if (allItems.length === 0) return bins;

    const uniquePrices = Array.from(
      new Set(allItems.map((item) => item.price.amount)),
    ).sort((a, b) => a - b);

    const step = Math.max(1, uniquePrices.length / BIN_COUNT);

    let lastMax = -1;
    for (let i = 0; i < BIN_COUNT; i++) {
      let targetIdx = Math.floor((i + 1) * step) - 1;
      if (i === BIN_COUNT - 1 || targetIdx >= uniquePrices.length) {
        targetIdx = uniquePrices.length - 1;
      }
      const currentMax = uniquePrices[targetIdx];
      bins.push({
        count: 0,
        minPrice: lastMax < 0 ? 0 : lastMax + 0.01,
        maxPrice: currentMax,
        height: 0,
      });
      lastMax = currentMax;
    }

    allItems.forEach((item) => {
      const price = item.price.amount;
      const bin = bins.find((b) => price <= b.maxPrice) || bins[BIN_COUNT - 1];
      bin.count++;
    });

    const maxCount = Math.max(...bins.map((b) => b.count), 1);
    bins.forEach((b) => {
      b.height = (b.count / maxCount) * 100;
    });

    return bins;
  }, [allItems]);

  const categories = useMemo(
    () => ["All", ...new Set(allItems.map((item) => item.category))],
    [allItems],
  );

  const itemCounts = useMemo(() => {
    const counts: Record<string, number> = { All: allItems.length };
    allItems.forEach((item) => {
      counts[item.category] = (counts[item.category] || 0) + 1;
    });
    return counts;
  }, [allItems]);

  const currentMaxPrice = maxPrice ?? absoluteMaxPrice;

  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      const matchesCategory =
        activeCategory === "All" || item.category === activeCategory;
      const matchesSearch =
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.shortDescription.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesPrice = item.price.amount <= currentMaxPrice;
      return matchesCategory && matchesSearch && matchesPrice;
    });
  }, [allItems, activeCategory, searchQuery, currentMaxPrice]);

  return {
    data: {
      filteredItems,
      categories,
      itemCounts,
      totalCount: filteredItems.length,
      absoluteMaxPrice,
      priceHistogram,
    },
    state: {
      activeCategory,
      searchQuery,
      maxPrice: currentMaxPrice,
      hasActiveFilters:
        activeCategory !== "All" ||
        searchQuery !== "" ||
        currentMaxPrice < absoluteMaxPrice,
    },
    actions: {
      setCategory: setActiveCategory,
      setSearchQuery: setSearchQuery,
      setMaxPrice: setMaxPrice,
      resetFilters,
    },
  };
};
